import React, { useEffect, useState } from 'react';
import FocusTables from './FocusTables';
import ScraperStatusMap from './ScraperStatusMap';
import ScraperGodView from './ScraperGodView';
import { useTheme } from '../../context/ThemeContext';
import { mockScraperData, mockFocusTableData } from './mockData';
import { scrapeMetadata } from './realScraperData';
import { ChevronUpIcon, ChevronDownIcon, ZoomInIcon, ZoomOutIcon, PaletteIcon, XIcon } from 'lucide-react';
import { TrendingDownIcon, TrendingUpIcon } from 'lucide-react';
const Dashboard: React.FC = () => {
  const {
    theme
  } = useTheme();
  const [timeRange, setTimeRange] = useState<'day' | 'week' | 'month'>('month');
  const [showFocusTables, setShowFocusTables] = useState(true);
  const [viewMode, setViewMode] = useState<'detail' | 'god'>('detail');
  const [showLegend, setShowLegend] = useState(false);
  const [colorFilter, setColorFilter] = useState<string | null>(null);
  // Filter data based on selected time range
  const getFilteredData = () => {
    const {
      scrapers,
      dailyData
    } = mockScraperData;
    if (timeRange === 'day') {
      // Only show today's data (last item in the array)
      return {
        scrapers,
        dailyData: dailyData.slice(-1)
      };
    } else if (timeRange === 'week') {
      // Show last 7 days
      return {
        scrapers,
        dailyData: dailyData.slice(-7)
      };
    } else {
      // Show full 28 days (month view)
      return mockScraperData;
    }
  };
  const filteredData = getFilteredData();
  // Ensure we're using the same color calculation for filtering and display
  const legendColors = [{
    color: '#7f8aa1',
    name: 'Grey',
    description: 'Data Unavailable'
  }, {
    color: '#ff00cc',
    name: 'Broken',
    description: '≥90% drop',
    border: null,
    icon: 'https://uploadthingy.s3.us-west-1.amazonaws.com/91ffpPark4Uc7sq1sq9thV/Icons-exclamation-triangle.png'
  }, {
    color: '#ef4444',
    name: 'Needs Attention',
    description: '50+ URL changes (requires review)',
    border: 'border border-red-500'
  }, {
    color: '#f97316',
    name: 'Major',
    description: '40-49 URL changes',
    border: null
  }, {
    color: '#f59e0b',
    name: 'Moderate',
    description: '30-39 URL changes',
    border: null
  }, {
    color: '#eab308',
    name: 'Minor',
    description: '20-29 URL changes',
    border: null
  }, {
    color: '#84cc16',
    name: 'Routine',
    description: '10-19 URL changes',
    border: null
  }, {
    color: '#22c55e',
    name: 'Minimal',
    description: '1-9 URL changes',
    border: null
  }, {
    color: '#4ade80',
    name: 'Stable',
    description: '0 URL changes',
    border: null
  }];
  return <div className={`p-6 min-h-full ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`} data-id="element-24">
      <div className="mb-6" data-id="element-25">
        {/* Header and stat cards in the same div with flex layout */}
        <div className="flex flex-wrap items-center justify-between" data-id="element-26">
          <h2 className="text-2xl font-extralight tracking-tight" data-id="element-27">
            Scraper Status Dashboard
          </h2>
          <div className="flex w-3/4 space-x-4" data-id="element-28">
            <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-gray-800/70 backdrop-blur-sm border border-gray-700/50' : 'bg-white/90 backdrop-blur-sm border border-gray-200'} flex-1`} data-id="element-29">
              <div className="flex justify-between items-center" data-id="element-30">
                <h4 className="font-light text-sm" data-id="element-31">Total URLs Scraped</h4>
                <span className={`px-2 py-0.5 text-xs rounded-full ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`} data-id="element-32">
                  Scrape #{scrapeMetadata.scrapeId}
                </span>
              </div>
              <div className="text-xl font-extralight text-teal-500" data-id="element-33">
                {scrapeMetadata.totalUrls.toLocaleString()}
              </div>
              <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} font-extralight`} data-id="element-34">
                +1.2% from yesterday
              </div>
            </div>
            <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-gray-800/70 backdrop-blur-sm border border-gray-700/50' : 'bg-white/90 backdrop-blur-sm border border-gray-200'} flex-1`} data-id="element-35">
              <div className="flex justify-between items-center" data-id="element-36">
                <h4 className="font-light text-sm" data-id="element-37">Active Scrapers</h4>
              </div>
              <div className="text-xl font-extralight text-fuchsia-500" data-id="element-38">
                {scrapeMetadata.activeScrapers}
              </div>
              <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} font-extralight`} data-id="element-39">
                {scrapeMetadata.totalScrapers - scrapeMetadata.activeScrapers}{' '}
                currently inactive
              </div>
            </div>
            <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-gray-800/70 backdrop-blur-sm border border-gray-700/50' : 'bg-white/90 backdrop-blur-sm border border-gray-200'} flex-1`} data-id="element-40">
              <div className="flex justify-between items-center" data-id="element-41">
                <h4 className="font-light text-sm" data-id="element-42">Error Rate</h4>
              </div>
              <div className="text-xl font-extralight text-yellow-500" data-id="element-43">
                3.8%
              </div>
              <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} font-extralight`} data-id="element-44">
                -0.5% from last week
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scraper Status Map Section - MOVED UP */}
      <div className="mt-8 mb-6" data-id="element-45">
        <div className="flex items-center justify-between mb-4" data-id="element-46">
          <h3 className="text-xl font-extralight" data-id="element-47">
            Scraper Status Map
            {timeRange === 'day' ? ' (Today)' : timeRange === 'week' ? ' (Last 7 Days)' : ''}
          </h3>
          <div className="flex space-x-2" data-id="element-48">
            <button onClick={() => setViewMode(viewMode === 'detail' ? 'god' : 'detail')} className={`px-3 py-1 rounded-md text-sm font-light flex items-center ${theme === 'dark' ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'}`} title={viewMode === 'detail' ? 'Switch to God View' : 'Switch to Detail View'} data-id="element-49">
              {viewMode === 'detail' ? <>
                  <ZoomOutIcon size={14} className="mr-1" data-id="element-50" />
                  God View
                </> : <>
                  <ZoomInIcon size={14} className="mr-1" data-id="element-51" />
                  Detail View
                </>}
            </button>
            {/* Legend Button */}
            <button onClick={() => setShowLegend(true)} className={`px-3 py-1 rounded-md text-sm font-light flex items-center ${colorFilter ? theme === 'dark' ? 'bg-fuchsia-900/50 text-fuchsia-300 border border-fuchsia-700' : 'bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-300' : theme === 'dark' ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'}`} title="Filter by Status" data-id="element-52">
              <PaletteIcon size={14} className="mr-1" data-id="element-53" />
              {colorFilter ? 'Filtered View' : 'Status Filter'}
            </button>
            <button onClick={() => setTimeRange('day')} className={`px-3 py-1 rounded-md text-sm font-light ${timeRange === 'day' ? theme === 'dark' ? 'bg-fuchsia-900/50 text-fuchsia-300 border border-fuchsia-700' : 'bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-300' : theme === 'dark' ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'}`} title="View today's scraper data only" data-id="element-54">
              Day
            </button>
            <button onClick={() => setTimeRange('week')} className={`px-3 py-1 rounded-md text-sm font-light ${timeRange === 'week' ? theme === 'dark' ? 'bg-fuchsia-900/50 text-fuchsia-300 border border-fuchsia-700' : 'bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-300' : theme === 'dark' ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'}`} title="View last 7 days of scraper data" data-id="element-55">
              Week
            </button>
            <button onClick={() => setTimeRange('month')} className={`px-3 py-1 rounded-md text-sm font-light ${timeRange === 'month' ? theme === 'dark' ? 'bg-fuchsia-900/50 text-fuchsia-300 border border-fuchsia-700' : 'bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-300' : theme === 'dark' ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'}`} title="View full 28 days of scraper data" data-id="element-56">
              Month
            </button>
          </div>
        </div>
        <div className="w-full" data-id="element-57">
          {viewMode === 'detail' ? <ScraperStatusMap scraperData={filteredData} viewMode={timeRange} colorFilter={colorFilter} data-id="element-58" /> : <ScraperGodView scraperData={mockScraperData} viewMode={timeRange} colorFilter={colorFilter} data-id="element-59" />}
        </div>
      </div>

      {/* Focus Tables Section */}
      <div className="mb-6" data-id="element-60">
        <div className={`flex items-center justify-between px-4 py-3 mb-3 rounded-lg border ${theme === 'dark' ? 'bg-gray-800/70 backdrop-blur-sm border-gray-700/50' : 'bg-white/90 backdrop-blur-sm border-gray-200'} cursor-pointer`} onClick={() => setShowFocusTables(!showFocusTables)} data-id="element-61">
          <div className="flex items-center" data-id="element-62">
            <div className={theme === 'dark' ? 'text-fuchsia-400' : 'text-fuchsia-600'} data-id="element-63">
              {showFocusTables ? <ChevronUpIcon size={18} data-id="element-64" /> : <ChevronDownIcon size={18} data-id="element-65" />}
            </div>
            <h3 className="font-light ml-2 flex items-center" data-id="element-66">
              <span data-id="element-67">Focus Tables:</span>
              <span className="text-red-500 ml-3 mr-1 flex items-center" data-id="element-68">
                <TrendingDownIcon size={16} className="mr-1" data-id="element-69" />
                Highest Decreased Counts
              </span>
              <span className="mx-2" data-id="element-70">|</span>
              <span className="text-teal-500 flex items-center" data-id="element-71">
                <TrendingUpIcon size={16} className="mr-1" data-id="element-72" />
                Highest Increased Counts
              </span>
            </h3>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`} data-id="element-73">
            {showFocusTables ? 'Hide' : 'Show'} All
          </span>
        </div>
        {showFocusTables && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-74">
            {/* Negative Impact Table */}
            <div className={`rounded-lg overflow-hidden border ${theme === 'dark' ? 'bg-gray-800/70 backdrop-blur-sm border-gray-700/50' : 'bg-white/90 backdrop-blur-sm border-gray-200'}`} data-id="element-75">
              <div className="max-h-72 overflow-y-auto" data-id="element-76">
                <FocusTables focusData={{
              positive: [],
              negative: [{
                scraperId: 97,
                scraperName: '097 Neighborhood Health RI',
                urlChange: 1342,
                percentChange: 95,
                previousCount: 1413,
                currentCount: 71,
                status: 'broken',
                amPm: 'PM',
                date: new Date().toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })
              }, {
                scraperId: 82,
                scraperName: '082 BCBS Highmark',
                urlChange: 718,
                percentChange: 50,
                previousCount: 1436,
                currentCount: 718,
                status: 'needsAttention',
                amPm: 'AM',
                date: new Date().toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })
              }, {
                scraperId: 11,
                scraperName: '011 BCBS Michigan',
                urlChange: 45,
                percentChange: 40,
                previousCount: 1200,
                currentCount: 1155,
                status: 'major',
                amPm: 'PM',
                date: new Date().toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })
              }, {
                scraperId: 118,
                scraperName: '118 TriCare',
                urlChange: 35,
                percentChange: 30,
                previousCount: 2099,
                currentCount: 2064,
                status: 'moderate',
                amPm: 'AM',
                date: new Date().toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })
              }, {
                scraperId: 28,
                scraperName: '028 BCBS Federal Employees (FEP)',
                urlChange: 25,
                percentChange: 25,
                previousCount: 996,
                currentCount: 971,
                status: 'minor',
                amPm: 'PM',
                date: new Date().toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })
              }]
            }} data-id="element-77" />
              </div>
            </div>
            {/* Positive Impact Table */}
            <div className={`rounded-lg overflow-hidden border ${theme === 'dark' ? 'bg-gray-800/70 backdrop-blur-sm border-gray-700/50' : 'bg-white/90 backdrop-blur-sm border-gray-200'}`} data-id="element-78">
              <div className="max-h-72 overflow-y-auto" data-id="element-79">
                <FocusTables focusData={{
              positive: [{
                scraperId: 51,
                scraperName: '051 BCBS Tennessee',
                urlChange: 52,
                percentChange: 650,
                previousCount: 8,
                currentCount: 60,
                status: 'needsAttention',
                amPm: 'AM',
                date: new Date().toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })
              }, {
                scraperId: 39,
                scraperName: '039 AlohaCare',
                urlChange: 35,
                percentChange: 39,
                previousCount: 90,
                currentCount: 125,
                status: 'moderate',
                amPm: 'PM',
                date: new Date().toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })
              }, {
                scraperId: 106,
                scraperName: '106 Centene Corporation',
                urlChange: 25,
                percentChange: 20,
                previousCount: 1118,
                currentCount: 1143,
                status: 'minor',
                amPm: 'AM',
                date: new Date().toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })
              }, {
                scraperId: 45,
                scraperName: '045 BCBS Louisiana',
                urlChange: 15,
                percentChange: 15,
                previousCount: 915,
                currentCount: 930,
                status: 'routine',
                amPm: 'PM',
                date: new Date().toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })
              }, {
                scraperId: 4,
                scraperName: '004 BCBS Arkansas',
                urlChange: 8,
                percentChange: 5,
                previousCount: 890,
                currentCount: 898,
                status: 'minimal',
                amPm: 'AM',
                date: new Date().toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })
              }],
              negative: []
            }} data-id="element-80" />
              </div>
            </div>
          </div>}
      </div>

      {/* Legend Modal */}
      {showLegend && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowLegend(false)} data-id="element-81">
          <div className={`relative p-5 rounded-lg shadow-lg max-w-md w-full ${theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`} onClick={e => e.stopPropagation()} data-id="element-82">
            <button className={`absolute top-2 right-2 p-1 rounded-full ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`} onClick={() => setShowLegend(false)} data-id="element-83">
              <XIcon size={18} data-id="element-84" />
            </button>
            <div className="mb-4" data-id="element-85">
              <h3 className="text-lg font-medium" data-id="element-86">Status Legend</h3>
              <p className="text-sm mt-1 font-light" data-id="element-87">
                Click on a color to filter the view
              </p>
            </div>
            <div className="space-y-2" data-id="element-88">
              {/* Show All option */}
              <div className={`flex items-center p-2 rounded-md cursor-pointer ${colorFilter === null ? theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100' : ''}`} onClick={() => {
            setColorFilter(null);
            setShowLegend(false);
          }} data-id="element-89">
                <div className="w-6 h-6 flex items-center justify-center border border-gray-400 rounded-md mr-2" data-id="element-90">
                  <span className="text-xs" data-id="element-91">All</span>
                </div>
                <span className="font-light" data-id="element-92">Show All Statuses</span>
              </div>
              {/* Color filters */}
              {legendColors.map(item => <div key={item.color + (item.name === 'Needs Attention' ? '-needs-attention' : '')} className={`flex items-center p-2 rounded-md cursor-pointer ${colorFilter === item.color ? theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100' : ''}`} onClick={() => {
            setColorFilter(item.color === colorFilter ? null : item.color);
            setShowLegend(false);
          }} data-id="element-93">
                  <span className={`w-6 h-6 mr-2 rounded-md ${item.border || ''}`} style={{
              backgroundColor: item.color
            }} data-id="element-94">
                    {item.icon && <img src={item.icon} alt="Warning" className="w-full h-full object-contain p-1" data-id="element-95" />}
                  </span>
                  <span className="font-light" data-id="element-96">
                    {item.name} - {item.description}
                  </span>
                </div>)}
            </div>
          </div>
        </div>}
    </div>;
};
export default Dashboard;